import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, Autocomplete, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MeetingRoom as MeetingRoomIcon,
  Groups as GroupsIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';

// ─── Tipos ───

interface FilaTotais {
  total_pacientes: number;
  espera_media_seg: number;
  espera_max_seg: number;
  consultorios: number;
  unidades: number;
}

interface FilaItem {
  codprofissional: number;
  codunidade: string;
  consultorio: string;
  unidade_nome: string;
  nomespecialidade: string;
  origem: string;
  aguardando: number;
  espera_media_seg: number;
  espera_max_seg: number;
}

interface FilasConsultorios {
  totais: FilaTotais;
  filas: FilaItem[];
  gerado_em: string;
}

interface PacienteItem {
  nompaciente: string;
  idade: number | null;
  consultorio: string;
  nomespecialidade: string;
  origem: string;
  dh_entrada_fmt: string;
  espera_seg: number;
}

interface Sala {
  id_sala: number;
  nomsala: string;
}

const REFRESH_MS = 60_000;

/** Formata segundos em texto amigavel: "2h 35min", "12 min", "<1 min". */
const fmtDuracao = (seg?: number | null): string => {
  if (seg == null || Number.isNaN(seg) || seg <= 0) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
  if (m > 0) return `${m} min`;
  return '<1 min';
};

const corEspera = (seg: number): 'error' | 'warning' | 'success' =>
  seg >= 3600 ? 'error' : seg >= 1800 ? 'warning' : 'success';

const filaKey = (f: FilaItem): string => `${f.codprofissional}-${f.codunidade}`;

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Linha da fila (com detalhe de pacientes ao expandir) ───

const FilaRow: React.FC<{
  fila: FilaItem;
  mostrarUnidade: boolean;
  aberta: boolean;
  onToggle: () => void;
  pacientes: PacienteItem[];
  pacientesLoading: boolean;
}> = ({ fila, mostrarUnidade, aberta, onToggle, pacientes, pacientesLoading }) => {
  const theme = useTheme();
  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '& > *': { borderBottom: aberta ? 'unset' : undefined } }} onClick={onToggle}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">{aberta ? <ArrowUpIcon /> : <ArrowDownIcon />}</IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" fontWeight={600}>{fila.consultorio}</Typography>
          <Typography variant="caption" color="text.secondary">
            {fila.nomespecialidade}{mostrarUnidade ? ` • ${fila.unidade_nome}` : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip size="small" variant="outlined" label={fila.origem} />
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight={700}>{fila.aguardando}</Typography>
        </TableCell>
        <TableCell align="right">
          <Chip size="small" variant="outlined" color={corEspera(fila.espera_media_seg)} label={fmtDuracao(fila.espera_media_seg)} />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDuracao(fila.espera_max_seg)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: aberta ? `1px solid ${theme.palette.divider}` : 'none' }} colSpan={6}>
          <Collapse in={aberta} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Pacientes em espera — {fila.consultorio}
              </Typography>
              {pacientesLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : pacientes.length ? (
                <TableContainer sx={{ maxHeight: 360, bgcolor: alpha(theme.palette.primary.main, 0.02), borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Paciente</TableCell>
                        <TableCell>Especialidade</TableCell>
                        <TableCell>Origem</TableCell>
                        <TableCell>Entrada na fila</TableCell>
                        <TableCell align="right">Tempo de espera</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pacientes.map((p, i) => (
                        <TableRow key={`${fila.codprofissional}-${i}`}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {p.nompaciente}
                            {p.idade != null ? <Typography component="span" variant="caption" color="text.secondary"> ({p.idade}a)</Typography> : ''}
                          </TableCell>
                          <TableCell>{p.nomespecialidade}</TableCell>
                          <TableCell><Chip size="small" variant="outlined" label={p.origem} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.dh_entrada_fmt}</TableCell>
                          <TableCell align="right">
                            <Chip size="small" variant="outlined" color={corEspera(p.espera_seg)} label={fmtDuracao(p.espera_seg)} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" variant="body2">Nenhum paciente em espera neste consultorio.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Pagina ───

const SaudeFilasConsultorios: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<FilasConsultorios | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtro Sala (lookup vazio por padrao; so e preenchido apos escolher a Unidade)
  const [salas, setSalas] = useState<Sala[]>([]);
  const [salasLoading, setSalasLoading] = useState(false);
  const [salaSelecionada, setSalaSelecionada] = useState<Sala | null>(null);

  // Detalhe (16.7): apenas uma fila expandida por vez
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pacientes, setPacientes] = useState<PacienteItem[]>([]);
  const [pacientesLoading, setPacientesLoading] = useState(false);

  const fetchRef = useRef<() => void>(() => {});

  // Lookup de salas: so e preenchido quando ha uma unidade selecionada
  useEffect(() => {
    setSalaSelecionada(null);
    if (!municipioAtivo?.id || !unidadeSelecionada?.codunidade) {
      setSalas([]);
      return;
    }
    let cancelled = false;
    setSalasLoading(true);
    saudeService
      .getSalas(municipioAtivo.id, unidadeSelecionada.codunidade)
      .then((d: Sala[]) => { if (!cancelled) setSalas(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setSalas([]); })
      .finally(() => { if (!cancelled) setSalasLoading(false); });
    return () => { cancelled = true; };
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getFilasConsultorios(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        sala: salaSelecionada ? String(salaSelecionada.id_sala) : undefined,
      });
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar as filas de consultorios.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, salaSelecionada]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar qualquer filtro
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  // 16.7: ao expandir uma fila (ou a cada atualizacao), busca os pacientes daquele consultorio
  useEffect(() => {
    if (!expanded || !municipioAtivo?.id || !data) { setPacientes([]); return; }
    const fila = data.filas.find((f) => filaKey(f) === expanded);
    if (!fila) { setExpanded(null); return; }
    let cancelled = false;
    setPacientesLoading(true);
    saudeService
      .getFilasConsultoriosPacientes(municipioAtivo.id, {
        profissional: String(fila.codprofissional),
        unidade: fila.codunidade,
        sala: salaSelecionada ? String(salaSelecionada.id_sala) : undefined,
      })
      .then((d: { lista: PacienteItem[] }) => { if (!cancelled) setPacientes(d?.lista || []); })
      .catch(() => { if (!cancelled) setPacientes([]); })
      .finally(() => { if (!cancelled) setPacientesLoading(false); });
    return () => { cancelled = true; };
    // data?.gerado_em mantem a lista expandida em sincronia com o auto-refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, data?.gerado_em, municipioAtivo?.id, salaSelecionada]);

  const totais = data?.totais;
  const filas = data?.filas || [];
  const mostrarUnidade = !unidadeSelecionada;

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar as filas de consultorios.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MeetingRoomIcon color="primary" /> Filas de Consultorios
            <Chip size="small" color="primary" variant="outlined" icon={<TodayIcon sx={{ fontSize: 16 }} />} label="Hoje" sx={{ ml: 0.5 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong>{salaSelecionada ? <> • Sala: <strong>{salaSelecionada.nomsala}</strong></> : ''}</>
              : 'Todas as unidades do municipio'}
            {' '}• Fila do dia de hoje (tempo real)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtro: sala */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Autocomplete
            size="small"
            options={salas}
            getOptionLabel={(o) => o.nomsala}
            value={salaSelecionada}
            onChange={(_, v) => setSalaSelecionada(v)}
            disabled={!unidadeSelecionada}
            loading={salasLoading}
            isOptionEqualToValue={(o, v) => o.id_sala === v.id_sala}
            sx={{ width: 240 }}
            renderInput={(params) => (
              <TextField {...params} label="Sala"
                placeholder={unidadeSelecionada ? 'Todas as salas' : 'Selecione a unidade'} />
            )}
          />
          {!unidadeSelecionada && (
            <Typography variant="caption" color="text.secondary">
              Selecione uma Unidade de Saude (acima) para filtrar por sala.
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><KpiCard label="Pacientes em espera" value={(totais?.total_pacientes ?? 0).toLocaleString('pt-BR')} icon={<GroupsIcon />} color="#1976d2" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Tempo medio de espera" value={fmtDuracao(totais?.espera_media_seg)} icon={<TimerIcon />} color="#ff9800" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Maior espera" value={fmtDuracao(totais?.espera_max_seg)} icon={<TrendingUpIcon />} color="#e53935" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Consultorios com fila" value={(totais?.consultorios ?? 0).toLocaleString('pt-BR')} icon={<MeetingRoomIcon />} color="#00838f" loading={loading} /></Grid>
      </Grid>

      {/* Filas de consultorios (16.6) — clique numa fila para ver os pacientes (16.7) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Filas por consultorio <Typography component="span" variant="caption" color="text.secondary">(clique numa fila para ver os pacientes em espera)</Typography>
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={320} />
          ) : filas.length ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Consultorio (profissional)</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell align="center">Aguardando</TableCell>
                    <TableCell align="right">Tempo medio de espera</TableCell>
                    <TableCell align="right">Maior espera</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.map((f) => {
                    const key = filaKey(f);
                    const aberta = expanded === key;
                    return (
                      <FilaRow
                        key={key}
                        fila={f}
                        mostrarUnidade={mostrarUnidade}
                        aberta={aberta}
                        onToggle={() => setExpanded(aberta ? null : key)}
                        pacientes={aberta ? pacientes : []}
                        pacientesLoading={aberta && pacientesLoading}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">Nenhum paciente em espera hoje.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Fila do dia de hoje • Espera = tempo desde a entrada na fila ate agora • Fontes: UBS + Pronto Atendimento${unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ''}`} />
    </Box>
  );
};

export default SaudeFilasConsultorios;
